import { lazy, Suspense, FC, useState, useEffect, MouseEventHandler } from 'react';
import { createFragmentContainer, graphql, RelayProp } from 'react-relay/legacy';

// components
import { Link } from 'dibs-elements/exports/Link';
const ReturnPolicyModal = lazy(
    () =>
        import(
            /* webpackChunkName: "ReturnPolicyRenderer" */ 'dibs-return-policy/exports/ReturnPolicyRenderer'
        )
);

// helpers
import * as tracking from 'dibs-tracking';

// styles
import styles from '../shared/PdpSharedDetail/PdpSharedDetail.scss';

import { PdpSharedDetailReturnPolicy_item$data } from './__generated__/PdpSharedDetailReturnPolicy_item.graphql';

type Props = {
    item: PdpSharedDetailReturnPolicy_item$data;
    itemId: string;
    userId?: string;
    relay: RelayProp;
};

const PdpSharedDetailReturnPolicyComponent: FC<Props> = ({ item, userId, itemId, relay }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        if (urlParams.get('modal') === 'returnPolicy') {
            setIsModalOpen(true);
        }
    }, []);

    const onClick: MouseEventHandler<HTMLAnchorElement> = e => {
        e.preventDefault();
        setIsModalOpen(true);

        // the following was originally in callback to this.setState
        const returnable = item?.returnPolicy?.accept;

        tracking.trackEvent({
            category: 'product',
            action: 'view return details',
            label: `pdp description|${returnable ? 'returns accepted' : 'returns not accepted'}`,
        });
    };

    const onClose = (): void => {
        setIsModalOpen(false);
    };

    const buyerCopy = item?.returnPolicy?.buyerCopy;
    const returnPolicySection = buyerCopy?.returnPolicySection?.htmlCopy || '';
    const returnPolicySectionCtaCopy = buyerCopy?.returnPolicySectionCtaCopy?.htmlCopy || '';
    const customizableCopy = buyerCopy?.customizableDetailReturnCopy?.htmlCopy || '';
    const showCustomizableCopy = !!customizableCopy;

    return (
        <span>
            {/* Item Return Policy */}
            <span
                className={styles.returnPolicy}
                dangerouslySetInnerHTML={{ __html: returnPolicySection }}
            />
            {showCustomizableCopy && (
                <>
                    {' '}
                    <span
                        className={styles.returnPolicy}
                        dangerouslySetInnerHTML={{ __html: customizableCopy }}
                    />
                </>
            )}
            {/* See Details */}{' '}
            <Link onClick={onClick} className={styles.printHide}>
                <div dangerouslySetInnerHTML={{ __html: returnPolicySectionCtaCopy }} />
            </Link>
            {/* Return Policy Modal */}
            {isModalOpen && (
                <Suspense fallback={null}>
                    <ReturnPolicyModal
                        itemIds={[itemId]}
                        userId={userId || ''}
                        environment={relay.environment}
                        isOpen
                        onClose={onClose}
                        isModal
                        trackingData={{ category: 'product', label: 'pdp returns - intl' }}
                    />
                </Suspense>
            )}
        </span>
    );
};

export const PdpSharedDetailReturnPolicy = createFragmentContainer(
    PdpSharedDetailReturnPolicyComponent,
    {
        item: graphql`
            fragment PdpSharedDetailReturnPolicy_item on Item {
                returnPolicy(userId: $userId) {
                    accept
                    buyerCopy {
                        returnPolicySection(
                            userCountryCode: $userCountryCode
                            isEuReturnPolicyVariant: $isEuReturnPolicyVariant
                        ) {
                            htmlCopy
                        }
                        returnPolicySectionCtaCopy {
                            htmlCopy
                        }
                        customizableDetailReturnCopy {
                            htmlCopy
                        }
                    }
                }
            }
        `,
    }
);
